import React, { useEffect, useContext, useRef } from 'react';
import { useProfileNavigate as useNavigate } from '../../contexts/ProfilePreviewNavigation';
import { UserContext } from '../../components/User/UserContext';
import { firebase } from '../../utils/firebase-config';
import { useSnackbar } from 'notistack';
import { resetModalStack } from '../../utils/modalHistory';
import notificationsCache from '../../utils/notificationsCache';
import { markNotificationRead } from '../../utils/notificationsDb';
import { getNotificationText, getTeamNotificationText, getCurrencyResetNotificationText } from '../../utils/notificationText';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { BellOff } from 'lucide-react';
import { USE_BACKEND } from '../../config/runtime';
import { notificationsApi } from '../../api/notifications';
import { subscribeRealtime } from '../../services/realtime';
import FramedAvatar from '../FramedAvatar';
import { inAppNotificationsEnabled } from '../../utils/inAppNotifications';

const NotificationListener = () => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t, language } = useLanguage();
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const userImages = useRef({});
  const mutedChats = useRef({ users: {}, groups: {} });
  const snackbarDragging = useRef(false);
  const snoozeUntil = useRef(0);
  const activeUserId = useRef(userData?.id || null);
  activeUserId.current = userData?.id || null;

  const isNotificationMuted = (notification) => {
    if (!notification) return false;
    if (notification.type === 'group_message' && notification.groupId) {
      return Boolean(mutedChats.current.groups?.[notification.groupId]);
    }
    if (notification.type === 'private_message' || notification.type === 'chat_message') {
      return Boolean(mutedChats.current.users?.[notification.fromUser]);
    }
    return false;
  };

  const markAsRead = async (notification) => {
    if (!userData?.id || !notification || (notification.read && notification.highlighted !== true)) return;
    notificationsCache.updateNotification(userData.id, notification.id, { read: true, highlighted: false });
    try {
      if (USE_BACKEND) await notificationsApi.read(notification.id);
      else await markNotificationRead(userData.id, notification);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const showNotification = async (notification) => {
    const recipientUserId = userData?.id;
    if (!inAppNotificationsEnabled(recipientUserId)) return;
    if (Date.now() < snoozeUntil.current) return;
    if (isNotificationMuted(notification)) return;

    const fromUserId = notification.fromUser;
    let userImage = notification.payload?.avatarUrl || notification.avatarUrl
      || notification.actor?.avatarUrl || userImages.current[fromUserId];
    if (!USE_BACKEND && !userImage && fromUserId) {
      try {
        const snapshot = await firebase.database().ref(`kunaiu_bank/${fromUserId}/img`).once('value');
        userImage = snapshot.val();
        userImages.current[fromUserId] = userImage;

        // console.log(fromUserId);
        
        // console.log('Fetched user image for userId', fromUserId, ':', userImage);
      } catch (error) {
        console.error('Error fetching user image:', error);
      }
    }
    if (!recipientUserId || activeUserId.current !== recipientUserId) return;
    snackbarDragging.current = false;

    const isTeamNotification = String(notification.type || '').toLowerCase() === 'team';
    const message = getCurrencyResetNotificationText(notification, language) || (isTeamNotification
      ? getTeamNotificationText(notification, language)
      : (notification.body || notification.title || getNotificationText(notification, t, language)));
    // console.log('Showing notification:', message);

    const handleClick = async () => {
      await markAsRead(notification);
      
      // Reset modal stack and clear history before navigating
      resetModalStack();
      
      // Replace current history to prevent back to modals
      window.history.replaceState(null, '');
      
      let destination = '/';
      if (notification.relatedEntityType === 'PROFILE_MEDIA_REVIEW') {
        destination = '/admin/profile-media';
      } else if (notification.groupId) {
        destination = `/messages/group/${notification.groupId}`;
      } else if (notification.type === 'chat_message' || notification.type === 'private_message') {
        destination = `/messages/chat/${notification.fromUser}`;
      } else if (notification.type === 'group_message' && notification.groupId) {
        destination = `/messages/group/${notification.groupId}`;
      } else if (notification.type === 'follow') {
        destination = `/profile/${notification.fromUser}`;
      } else if (notification.type === 'coins_with_comment') {
        if (notification.postId && notification.commentId) {
          destination = `/post/${notification.postId}/comment/${notification.commentId}/single`;
        } else if (notification.postId) {
          destination = `/post/${notification.postId}`;
        }
      } else if (notification.postId) {
        if (notification.commentId && notification.replyId) {
          destination = `/post/${notification.postId}/comment/${notification.commentId}/reply/${notification.replyId}/single`;
        } else if (notification.commentId) {
          destination = `/post/${notification.postId}/comment/${notification.commentId}/single`;
        } else {
          destination = `/post/${notification.postId}`;
        }
      } else {
        destination = '/notifications';
      }
      
      // Navigate with replace to clear modal history
      const navOptions = { replace: true };
      if (notification.preloadedContent) {
        navOptions.state = { preloadedContent: notification.preloadedContent };
      }
      navigate(destination, navOptions);
    };

    let key;
    const content = (
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        dragMomentum={false}
        onDragStart={() => { snackbarDragging.current = true; }}
        onDragEnd={(e, info) => {
          if (Math.abs(info.offset.x) > 55 || Math.abs(info.velocity.x) > 500) {
            snoozeUntil.current = Date.now() + 5 * 60 * 1000;
            closeSnackbar(key);
            return;
          }
          window.setTimeout(() => { snackbarDragging.current = false; }, 0);
        }}
        whileTap={{ cursor: "grabbing" }}
        style={{ display: 'flex', alignItems: 'flex-start', direction: dir, width: '100%', padding: '4px', position: 'relative', touchAction: 'pan-y', cursor: 'grab' }}
        onClick={async (e) => {
          if (snackbarDragging.current) return;
          closeSnackbar(key);
          await handleClick();
        }}
      >
        {(userImage || notification.actor?.frameUrl) && <span style={{ width: 40, height: 40, flexShrink: 0, marginInlineEnd: 12 }}><FramedAvatar src={userImage} alt={notification.fromUserName || ''} size={40} frameUrl={notification.actor?.frameUrl || notification.frame_url} frameBorderRadius={notification.actor?.frameBorderRadius || notification.frame_border_radius} frameFit={notification.actor?.frameFit || notification.frame_fit} sensitive={notification.actor?.sensitiveAvatar || notification.sensitive_img} /></span>}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, color: '#111', fontSize: '0.95rem' }}>
              {(notification.type === 'chat_message' || notification.type === 'private_message' || notification.type === 'group_message')
                ? t('notifications_page_toast_from', { name: notification.fromUserName || t('notifications_page_unknown_user') })
                : t('notifications_page_toast_new')}
            </h4>
            <button
              onClick={(e) => {
                e.stopPropagation();
                snoozeUntil.current = Date.now() + 5 * 60 * 1000;
                closeSnackbar(key);
              }}
              style={{
                background: 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '8px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                color: '#666',
                transition: 'background 0.2s',
              }}
              title={t('notifications_page_toast_snooze_title')}
            >
              <BellOff size={14} /> {t('notifications_page_toast_snooze')}
            </button>
          </div>
          <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#444', lineHeight: 1.4 }}>{message}</p>
        </div>
      </motion.div>
    );

    key = enqueueSnackbar(content, {
      variant: 'default',
      style: { 
        backgroundColor: '#fff', 
        borderRadius: '12px', 
        boxShadow: '0 4px 15px rgba(0,0,0,0.15)', 
        direction: dir,
        minWidth: '300px',
        padding: '12px 14px'
      },
      autoHideDuration: 4000,
    });
  };


  const showCurrentNotification = useRef(showNotification);
  showCurrentNotification.current = showNotification;
  useEffect(() => {
    if (!USE_BACKEND || !userData?.id) return undefined;
    const seen = new Set();
    return subscribeRealtime(event => {
      const value = event.data;
      if (event.type !== 'notification.created' || !value?.id || value.read || seen.has(value.id)) return;
      seen.add(value.id);
      if (seen.size > 100) seen.delete(seen.values().next().value);
      void showCurrentNotification.current({ ...value, type: String(value.type || '').toLowerCase(),
        fromUser: value.actor?.id, fromUserName: value.actor?.displayName,
        postId: value.payload?.postId || (value.relatedEntityType === 'POST' ? value.relatedEntityId : undefined),
        commentId: value.payload?.commentId || (value.relatedEntityType === 'COMMENT' ? value.relatedEntityId : undefined) });
    });
  }, [userData?.id]);

  // Muted-chat subscription intentionally restarts only when the account changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (USE_BACKEND || !userData?.id) return undefined;
    const ref = firebase.database().ref(`kunaiu_bank/${userData.id}/mutedChats`);
    const listener = (snapshot) => {
      const value = snapshot.val() || {};
      mutedChats.current = {
        users: value.users || {},
        groups: value.groups || {},
      };
    };
    ref.on('value', listener);
    return () => ref.off('value', listener);
  }, [userData?.id]);

  // Firebase notification subscription intentionally restarts only when the account changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (USE_BACKEND || !userData?.id) return undefined;
    const listenerUserId = userData.id;
    closeSnackbar();
    let initializedForUser = false;
    let knownNotificationIds = new Set();

    const notificationsRef = firebase.database().ref(`Notifications/${listenerUserId}`)
      .orderByChild('timestamp')
      .limitToLast(100);

    const handleNotifications = (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        if (!initializedForUser) {
          initializedForUser = true;
          notificationsCache.set(listenerUserId, []);
        }
        return;
      }

      const notifs = Object.entries(data)
        .map(([key, value]) => ({ id: key, ...value }))
        .sort((a, b) => b.timestamp - a.timestamp);

      const enrichNotificationWithContent = async (notif) => {
        try {
          const content = {};
          if (notif.postId) {
            const postSnap = await firebase.database().ref(`posts/${notif.postId}`).once('value');
            if (postSnap.exists()) content.post = { id: notif.postId, ...postSnap.val() };
          }
          if (notif.postId && notif.commentId) {
            const commentSnap = await firebase.database().ref(`posts/${notif.postId}/comments/${notif.commentId}`).once('value');
            if (commentSnap.exists()) content.comment = { id: notif.commentId, ...commentSnap.val() };
          }
          if (notif.postId && notif.commentId && notif.replyId) {
            const replySnap = await firebase.database().ref(`posts/${notif.postId}/comments/${notif.commentId}/replies/${notif.replyId}`).once('value');
            if (replySnap.exists()) content.reply = { id: notif.replyId, ...replySnap.val() };
          }
          return { ...notif, preloadedContent: content };
        } catch (error) {
          console.error('Error fetching notification content:', error);
          return notif;
        }
      };

      // The first value event is only a baseline for this account. Firebase
      // replays old rows on login/subscription, and those must never toast.
      if (!initializedForUser) {
        initializedForUser = true;
        knownNotificationIds = new Set(notifs.map((notification) => notification.id));
        notificationsCache.set(listenerUserId, notifs);
        return;
      }

      const newNotifications = notifs
        .filter((notification) => !knownNotificationIds.has(notification.id))
        .sort((a, b) => a.timestamp - b.timestamp);
      notifs.forEach((notification) => knownNotificationIds.add(notification.id));

      newNotifications.forEach((notification) => {
        enrichNotificationWithContent(notification).then((enriched) => {
          notificationsCache.addNotification(listenerUserId, enriched);
          showNotification(enriched);
        });
      });
    };

    notificationsRef.on('value', handleNotifications);

    return () => {
      notificationsRef.off('value', handleNotifications);
      closeSnackbar();
    };
  }, [userData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // This component doesn't render anything
};

export default NotificationListener;
